/**
 * Time & Material per-role rate helpers for ProjectFormPage (quick 261103-rwl).
 * Serves both the create payload and the edit (PATCH) payload (quick 261103-rwn).
 *
 * A non-billable T&M project is never invoiced, so per-role hourly rates are
 * optional there. A role row without a rate is still sent, with the rate
 * normalised to '0': project_overage_rates.hourly_rate is NUMERIC NOT NULL,
 * the backend body schema pattern (^[0-9]+(\.[0-9]{1,2})?$) already accepts
 * '0', and every billing consumer treats a 0 rate as "unpriced".
 */

/** Rate sent to the API when a role is picked but its rate is left blank. */
export const BLANK_TM_RATE = '0';

export interface TmRateRowInput {
  roleId: string;
  hourlyRate: string;
}

export interface TmOverageRatePayload {
  roleId: number;
  hourlyRate: string;
}

/** Rates are required unless the project is explicitly non-billable. */
export function isTmRateRequired(billable: boolean | undefined): boolean {
  return billable ?? true;
}

/**
 * Build the overageRates payload for a T&M project.
 * - billable: only rows with a role AND a non-blank rate (original behaviour).
 * - non-billable: every row with a role; a blank rate becomes '0'.
 */
export function buildTmOverageRates(
  rows: TmRateRowInput[],
  billable: boolean | undefined,
): TmOverageRatePayload[] {
  const required = isTmRateRequired(billable);
  const result: TmOverageRatePayload[] = [];
  for (const row of rows) {
    if (!row.roleId) continue;
    const rate = row.hourlyRate.trim();
    if (required && !rate) continue;
    result.push({
      roleId: parseInt(row.roleId, 10),
      hourlyRate: rate || BLANK_TM_RATE,
    });
  }
  return result;
}

/**
 * Payload fragment carrying overageRates for a Time & Material project, shared
 * by create and edit. Empty object for any other payment type so SLA Balicek /
 * Fixed Price / Default payloads are untouched. For T&M the key is ALWAYS
 * present (possibly an empty array) so the backend can reject a billable
 * project with no usable rate instead of the field being silently skipped.
 */
export function tmOverageRatesPatch(
  isTimeAndMaterial: boolean,
  rows: TmRateRowInput[],
  billable: boolean | undefined,
): { overageRates?: TmOverageRatePayload[] } {
  if (!isTimeAndMaterial) return {};
  return { overageRates: buildTmOverageRates(rows, billable) };
}

/**
 * True when an API error message is the Time & Material "at least one per-role
 * rate" gate. Deliberately does NOT match the SLA Balicek over-limit message
 * ("... requires at least one overage rate (PAY-03)"), which has no mention of
 * Time & Material.
 */
export function isTmMissingRatesError(message: string): boolean {
  if (/time\s*&\s*material.*at least one.*rate/i.test(message)) return true;
  return message.includes('PAY-TM') && /rate/i.test(message);
}
